import { useCallback, useMemo } from 'react';
import { FiltersParams } from 'types/params';
import { ModifiersPayload } from 'controllers/SearchController/types';
import {
    FirstLevelItemObject,
    SecondLevelItemObject,
} from 'components/Filters/context/FiltersContext/types';

export interface useFilterStateProps {
    filterType: FiltersParams;
    activeFilters: string[];
    totalNumberOfResults: number;
    addFilter: (modifiers: ModifiersPayload[]) => void;
    removeFilter: (modifiers: ModifiersPayload[]) => void;
}

export interface useFilterStateResult {
    isFilterChecked: (filterCode: string) => boolean;
    isFilterDisabled: (
        filter: FirstLevelItemObject | SecondLevelItemObject,
    ) => boolean;
    toggleFilter: (filterCode: string) => void;
    getSubFamiliesToDisplay: (
        filters: FirstLevelItemObject[],
    ) => FirstLevelItemObject[];
}

export const useFilterState = ({
    activeFilters,
    totalNumberOfResults,
    addFilter,
    removeFilter,
    filterType,
}: useFilterStateProps) => {
    const checkedFilters = useMemo(
        () => new Set(activeFilters),
        [activeFilters],
    );

    const isFilterChecked = useCallback(
        (filterCode: string) => checkedFilters.has(filterCode),
        [checkedFilters],
    );
    const isFilterDisabled = useCallback(
        (filter: FirstLevelItemObject | SecondLevelItemObject) => {
            const isChecked = isFilterChecked(filter.code);
            const hasResults = (filter.numberOfResults ?? 0) !== 0;
            const hasSameNumberOfResults =
                filter.numberOfResults === totalNumberOfResults;

            return !isChecked && (!hasResults || hasSameNumberOfResults);
        },
        [totalNumberOfResults, isFilterChecked],
    );

    const getSubFamiliesToDisplay = useCallback(
        (filters: FirstLevelItemObject[]) => {
            const hasCheckedFilter = filters.some((filter) =>
                isFilterChecked(filter.code),
            );

            if (!hasCheckedFilter) {
                return filters;
            }

            return filters.filter((filter) => {
                const isChecked = isFilterChecked(filter.code);

                const isSubFilterChecked =
                    filter.list?.some((subFilter) =>
                        isFilterChecked(subFilter.code),
                    ) ?? false;

                return isChecked || isSubFilterChecked;
            });
        },
        [isFilterChecked],
    );

    const toggleFilter = useCallback(
        (filterCode: string) => {
            const isChecked = isFilterChecked(filterCode);
            const action = isChecked ? removeFilter : addFilter;

            action([[filterType, filterCode]]);
        },
        [filterType, isFilterChecked, addFilter, removeFilter],
    );

    return {
        isFilterChecked,
        isFilterDisabled,
        toggleFilter,
        getSubFamiliesToDisplay,
    };
};
